import { toaster } from 'components/v2/toast';
import EpdCompiler from 'pages/v2/compiler/EpdCompiler';
import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import { useCheckReadEpdAccess, useVisibleEpdCopy } from 'services/api/queries';
import { getApiErrorDetail, getApiErrorStatus } from 'util/apiErrorUtils';

import EpdWizard from './EpdWizard';

const EPD_NOT_FOUND_DETAIL = 'Could not find the EPD.';

const WizardSelector: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const visibleCopyQuery = useVisibleEpdCopy(id);
  const userHasAccessToViewEpd = useCheckReadEpdAccess(id);

  // Wait for this mount's answer: a cached one may name a copy that has been switched since.
  if (!visibleCopyQuery.isFetchedAfterMount) {
    return null;
  }

  if (visibleCopyQuery.isError) {
    const isNotFound = getApiErrorStatus(visibleCopyQuery.error) === 404;
    toaster({
      severity: 'error',
      summary: isNotFound ? 'EPD not found' : 'Could not open the EPD',
      details: getApiErrorDetail(visibleCopyQuery.error) ?? (isNotFound ? EPD_NOT_FOUND_DETAIL : undefined),
      // Stable id so a re-render / StrictMode double-invoke does not stack duplicate toasts.
      options: { toastId: 'epd-visible-copy-error' },
    });
    return <Redirect to={EpdRoutes.dashboard} />;
  }

  const visibleCopy = visibleCopyQuery.data;
  if (!visibleCopy) {
    return null;
  }

  // The two copies of a migrated EPD may have different ids - always follow the id the server returned.
  if (visibleCopy.format === 'dynamic') {
    return <Redirect to={EpdLinks.dynamicForm(visibleCopy.id)} />;
  }

  if (visibleCopy.id.toLowerCase() !== id.toLowerCase()) {
    return <Redirect to={EpdLinks.epd(visibleCopy.id)} />;
  }

  if (userHasAccessToViewEpd.isSuccess && userHasAccessToViewEpd.data === false) {
    toaster({ severity: 'error', summary: 'Access denied', details: 'You do not have permission to view this EPD.' });
    return <Redirect to={EpdRoutes.dashboard} />;
  }

  if (userHasAccessToViewEpd.isSuccess) {
    return visibleCopy.format === 'compiler' ? <EpdCompiler /> : <EpdWizard />;
  }

  return null;
};

export default WizardSelector;
